'use client';

import { useEffect } from 'react';
import { useRouter } from '@/i18n/navigation';
import { useLocale, useTranslations } from 'next-intl';

/**
 * Global error boundary for all pages
 * Catches and handles errors that occur during rendering or data fetching
 */
export default function Error({ error, reset }) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('Errors.Global');

  useEffect(() => {
    // Log error to console
    console.error('Application error:', error);
  }, [error]);

  return (
    <div style={{
      minHeight: 'calc(100vh - 70px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      marginTop: '70px'
    }}>
      <div style={{
        maxWidth: '600px',
        textAlign: 'center',
        backgroundColor: '#FFFFFF',
        padding: '48px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Error Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#FEE2E2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="#DC2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Error Title */}
        <h1 style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '32px',
          fontWeight: '600',
          color: '#212121',
          marginBottom: '16px'
        }}>
          {t('title')}
        </h1>

        {/* Error Description */}
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          color: '#606576',
          lineHeight: '150%',
          marginBottom: '32px'
        }}>
          {t('body')}
        </p>

        {/* Error Details (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <p style={{
              fontFamily: 'monospace',
              fontSize: '14px',
              color: '#DC2626',
              margin: 0,
              wordBreak: 'break-word'
            }}>
              {error.message || t('unknownError')}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={reset}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1643E0',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0F2DB3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#1643E0'}
          >
            {t('tryAgain')}
          </button>

          <button
            onClick={() => router.push('/', { locale })}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#1643E0',
              border: '1px solid #1643E0',
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(22, 67, 224, 0.04)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            {t('goHome')}
          </button>
        </div>
      </div>
    </div>
  );
}
